import { isMobileOnly } from 'react-device-detect'
import { useTheme } from '@emotion/react'
import { RevenueCenter as RevenueCenterType } from '@open-tender/types'
import {
  getRevenueCenterStatus,
  makeFullAddress,
  stripTags,
} from '@open-tender/utils'
import {
  selectContent,
  selectDisplaySettings,
  selectGroupOrder,
  selectOrder,
  useAppSelector,
} from '@open-tender/cloud'
import { AlertCircle, Clock, MapPin, Phone } from 'components/icons'
import RevenueCenterOrder from './RevenueCenterOrder'
import RevenueCenterAction from './RevenueCenterAction'
import {
  RevenueCenterActions,
  RevenueCenterContent,
  RevenueCenterDesc,
  RevenueCenterDistance,
  RevenueCenterDistanceContainer,
  RevenueCenterFlex,
  RevenueCenterFlexContent,
  RevenueCenterHeader,
  RevenueCenterStatus,
  RevenueCenterTitle,
  RevenueCenterView,
} from './RevenueCenter.styled'
import { useMemo } from 'react'
import LazyRevenueCenterImage from './LazyRevenueCenterImage'

const RevenueCenter = ({
  revenueCenter,
  showImage,
  isActive = false,
  isMenu,
  isLanding,
  style = undefined,
}: {
  revenueCenter: RevenueCenterType
  showImage?: boolean
  isActive?: boolean
  isMenu?: boolean
  isLanding?: boolean
  style?: React.CSSProperties
}) => {
  const theme = useTheme()
  const hasBox = theme.cards.default.bgColor !== 'transparent'
  const { cartGuest } = useAppSelector(selectGroupOrder)
  const { serviceType } = useAppSelector(selectOrder)
  const { storePhone } = useAppSelector(selectDisplaySettings) || {
    storePhone: true,
  }
  const { revenueCenters } = useAppSelector(selectContent) ?? {}
  const isFullAddress = revenueCenters?.display_full_address ?? false
  const { address, images, hours, is_outpost, description } = revenueCenter
  const smallImg = images?.find((i) => i.type === 'SMALL_IMAGE')
  const largeImg = images?.find((i) => i.type === 'SMALL_IMAGE')
  const bgImage = smallImg?.url || largeImg?.url
  const phoneUrl = address.phone ? `tel:${address.phone}` : null
  const desc = description ? stripTags(description) : null
  const hoursDesc = hours.description ? stripTags(hours.description) : null
  const iconProps = { size: 14 }
  const hoursDescIcon = is_outpost ? (
    <AlertCircle {...iconProps} />
  ) : (
    <Clock {...iconProps} />
  )
  const distance =
    revenueCenter.distance !== null && revenueCenter.distance !== undefined
      ? revenueCenter.distance
      : null

  const statusMessage = useMemo<string | null>(
    () => getRevenueCenterStatus(revenueCenter, serviceType || 'WALKIN'),
    [revenueCenter, serviceType]
  )

  return (
    <RevenueCenterView
      style={style}
      hasBox={hasBox}
      isMenu={isMenu}
      isActive={isActive}
    >
      {showImage && !isMobileOnly && (
        <LazyRevenueCenterImage imageUrl={bgImage} />
      )}
      <RevenueCenterContent
        showImage={showImage && !isMobileOnly}
        hasBox={hasBox}
      >
        <div>
          <RevenueCenterFlex>
            <RevenueCenterFlexContent>
              <RevenueCenterHeader>
                <RevenueCenterTitle as="h3" className="title">
                  {revenueCenter.name}
                </RevenueCenterTitle>
                <RevenueCenterDistanceContainer>
                  {!!revenueCenter.user && serviceType === 'PICKUP' && (
                    <RevenueCenterDistance as="span" color="tertiary">
                      {revenueCenter.user.distance_in_miles.toFixed(2)} miles
                      away
                    </RevenueCenterDistance>
                  )}
                  {!!distance && statusMessage ? (
                    <RevenueCenterDistance as="span" color="tertiary">
                      &bull;
                    </RevenueCenterDistance>
                  ) : null}
                  <RevenueCenterStatus
                    color={statusMessage === 'Open Now' ? 'success' : 'alert'}
                    as="span"
                  >
                    {statusMessage}
                  </RevenueCenterStatus>
                </RevenueCenterDistanceContainer>
              </RevenueCenterHeader>
              <RevenueCenterActions>
                <a
                  href={revenueCenter.directions_url || ''}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <RevenueCenterAction
                    icon={<MapPin {...iconProps} />}
                    text={
                      isFullAddress ? makeFullAddress(address) : address.street
                    }
                  />
                </a>
                {storePhone && phoneUrl && !isMobileOnly && (
                  <a href={phoneUrl} rel="noopener noreferrer" target="_blank">
                    <RevenueCenterAction
                      icon={<Phone {...iconProps} />}
                      text={address.phone}
                    />
                  </a>
                )}
                {hoursDesc && (
                  <RevenueCenterAction
                    icon={hoursDescIcon}
                    text={hoursDesc}
                    arrow={null}
                  />
                )}
              </RevenueCenterActions>
            </RevenueCenterFlexContent>
          </RevenueCenterFlex>
          {desc && (
            <RevenueCenterDesc>
              <p>{desc}</p>
            </RevenueCenterDesc>
          )}
          {!cartGuest && (
            <RevenueCenterOrder
              statusMessage={statusMessage}
              revenueCenter={revenueCenter}
              isMenu={isMenu}
              isLanding={isLanding}
            />
          )}
        </div>
      </RevenueCenterContent>
    </RevenueCenterView>
  )
}

export default RevenueCenter
